// src/utils/openaiUtils.ts
import axios from "axios";
import { getApiBaseUrl } from "../config/api";

// --- Dictation post-process (capitalize + abbreviate cm/mm) ---

interface PostprocessDictationResponseData {
  status: number;
  message: string;
  user_id: string;
  processed_text?: string;
}

export const postprocessDictation = async (
  transcript: string,
  userId: string,
  token: string,
): Promise<string> => {
  if (!userId || !token) {
    throw new Error("User ID and authentication token are required.");
  }
  if (!transcript.trim()) {
    throw new Error("Transcript is empty.");
  }

  const isLocalhost = window.location.hostname === "localhost";
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (!isLocalhost) headers["Authorization"] = `Bearer ${token}`;

  const response = await axios.post<PostprocessDictationResponseData>(
    `${getApiBaseUrl()}/api/postprocess_dictation`,
    { user_id: userId, transcript },
    // 8s matches voice-adjust. Both hit gpt-5.4-nano with the same
    // reasoning_effort + max_completion_tokens, but cold-start latency
    // for the first call sits near the old 5s threshold and was timing
    // out reliably while voice-adjust (called second after the model
    // warms up) cleared its budget.
    { headers, timeout: 8000 },
  );

  if (
    response.data &&
    response.data.status === 200 &&
    typeof response.data.processed_text === "string" &&
    response.data.processed_text.trim().length > 0
  ) {
    return response.data.processed_text;
  }
  throw new Error(response.data?.message ?? "Postprocess failed");
};

// --- Voice-Adjust: rewrite a finding phrase from a voice instruction ---

interface VoiceAdjustResponseData {
  status: number;
  message: string;
  user_id: string;
  modified_text?: string;
}

export const voiceAdjust = async (
  originalText: string,
  instruction: string,
  userId: string,
  token: string,
): Promise<string> => {
  if (!userId || !token) {
    throw new Error("User ID and authentication token are required.");
  }
  if (!originalText.trim()) throw new Error("Original text is empty.");
  if (!instruction.trim()) throw new Error("Instruction is empty.");

  const isLocalhost = window.location.hostname === "localhost";
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (!isLocalhost) headers["Authorization"] = `Bearer ${token}`;

  const response = await axios.post<VoiceAdjustResponseData>(
    `${getApiBaseUrl()}/api/voice_adjust`,
    { user_id: userId, original_text: originalText, instruction },
    { headers, timeout: 8000 },
  );

  if (
    response.data &&
    response.data.status === 200 &&
    typeof response.data.modified_text === "string"
  ) {
    return response.data.modified_text;
  }
  throw new Error(response.data?.message ?? "Voice adjust failed");
};
